import {NotificationType, Tooltip} from 'argo-ui';
import classNames from 'classnames';
import * as moment from 'moment';
import * as React from 'react';
import {Cluster} from '../../../shared/components';
import {ContextApis, AuthSettingsCtx} from '../../../shared/context';
import * as models from '../../../shared/models';
import {NoticeIcon} from '../application-notice/notice-icon';
import {ApplicationURLs} from '../application-urls';
import * as AppUtils from '../utils';
import {getAppDefaultSource, OperationState, getApplicationLinkURL, getManagedByURL, MANAGED_BY_URL_INVALID_TEXT, MANAGED_BY_URL_INVALID_TOOLTIP} from '../utils';
import {isValidManagedByURL} from '../../../shared/utils';
import {services} from '../../../shared/services';
import {ViewPreferences} from '../../../shared/services';

export interface ApplicationTileProps {
    app: models.Application;
    selected: boolean;
    pref: ViewPreferences;
    ctx: ContextApis;
    tileRef?: React.RefObject<HTMLDivElement>;
    syncApplication: (appName: string, appNamespace: string) => void;
    refreshApplication: (appName: string, appNamespace: string) => void;
    deleteApplication: (appName: string, appNamespace: string) => void;
}

export const ApplicationTile = ({app, selected, pref, ctx, tileRef, syncApplication, refreshApplication, deleteApplication}: ApplicationTileProps) => {
    const useAuthSettingsCtx = React.useContext(AuthSettingsCtx);
    const favList = pref.appList.favoritesAppList || [];

    const source = getAppDefaultSource(app);
    const isOci = source?.repoURL?.startsWith('oci://');
    const targetRevision = source ? source.targetRevision || 'HEAD' : 'Unknown';
    const linkInfo = getApplicationLinkURL(app, ctx.baseHref);
    const healthStatus = app.status.health.status;
    const managedByURL = getManagedByURL(app);
    const managedByURLInvalid = !!managedByURL && !isValidManagedByURL(managedByURL);

    const handleFavoriteToggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (favList?.includes(app.metadata.name)) {
            favList.splice(favList.indexOf(app.metadata.name), 1);
        } else {
            favList.push(app.metadata.name);
        }
        services.viewPreferences.updatePreferences({appList: {...pref.appList, favoritesAppList: favList}});
    };

    const handleExternalLinkClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (managedByURLInvalid) {
            ctx.notifications.show({
                content: (
                    <div>
                        <div style={{fontWeight: 600}}>{MANAGED_BY_URL_INVALID_TEXT}</div>
                        <div style={{marginTop: 6}}>{MANAGED_BY_URL_INVALID_TOOLTIP}</div>
                    </div>
                ),
                type: NotificationType.Warning
            });
            return;
        }
        if (linkInfo.isExternal) {
            window.open(linkInfo.url, '_blank', 'noopener,noreferrer');
        } else {
            ctx.navigation.goto(`/${AppUtils.getAppUrl(app)}`);
        }
    };

    const labels = Object.keys(app.metadata.labels || {}).map(label => `${label}=${app.metadata.labels[label]}`);
    const isFavorite = favList?.includes(app.metadata.name);
    const lastSync = app.status.operationState && (app.status.operationState.finishedAt || app.status.operationState.startedAt);
    const stop = (fn: () => void) => (e: React.MouseEvent) => {
        e.stopPropagation();
        fn();
    };

    return (
        <div
            ref={tileRef}
            className={`sx-app-card applications-list__entry applications-list__entry--health-${healthStatus} ${selected ? 'applications-tiles__selected' : ''}`}
            onClick={e => ctx.navigation.goto(`/${AppUtils.getAppUrl(app)}`, {view: pref.appDetails.view}, {event: e})}>
            <div className={`sx-app-card__head qe-applications-list-${AppUtils.appInstanceName(app)}`}>
                <span className='sx-app-card__icon' aria-hidden='true'>
                    <i className={'icon argo-icon-' + (source?.chart != null ? 'helm' : isOci ? 'oci' : 'git')} />
                </span>
                <div className='sx-app-card__title'>
                    <Tooltip content={AppUtils.appInstanceName(app)}>
                        <span className='sx-app-card__name'>{AppUtils.appQualifiedName(app, useAuthSettingsCtx?.appsInAnyNamespaceEnabled)}</span>
                    </Tooltip>
                    <span className='sx-app-card__sub'>
                        {app.spec.project} · {app.spec.destination.namespace || 'default'}
                    </span>
                </div>
                <NoticeIcon annotations={app.metadata.annotations} />
                <div className='sx-app-card__links'>
                    <ApplicationURLs urls={app.status.summary?.externalURLs} />
                    <button
                        type='button'
                        className={`sx-icon-button ${managedByURLInvalid ? 'sx-icon-button--warn' : ''}`}
                        onClick={handleExternalLinkClick}
                        title={managedByURLInvalid ? MANAGED_BY_URL_INVALID_TEXT : managedByURL ? `Managed by: ${managedByURL}` : 'Open application'}
                        aria-label='Open application'>
                        <i className='fa fa-arrow-up-right-from-square' />
                    </button>
                    <button
                        type='button'
                        className={`sx-icon-button ${isFavorite ? 'sx-icon-button--fav' : ''}`}
                        title={isFavorite ? 'Remove favorite' : 'Add favorite'}
                        aria-label={isFavorite ? 'Remove favorite' : 'Add favorite'}
                        onClick={handleFavoriteToggle}>
                        <i className={isFavorite ? 'fas fa-star' : 'far fa-star'} />
                    </button>
                </div>
            </div>

            <div className='sx-app-card__status' qe-id='applications-tiles-health-status'>
                <span className={`sx-chip sx-chip--health-${(healthStatus || 'unknown').toLowerCase()}`}>
                    <AppUtils.HealthStatusIcon state={app.status.health} /> {healthStatus}
                </span>
                {app.status.sourceHydrator?.currentOperation && (
                    <span className='sx-chip'>
                        <AppUtils.HydrateOperationPhaseIcon operationState={app.status.sourceHydrator.currentOperation} /> {app.status.sourceHydrator.currentOperation.phase}
                    </span>
                )}
                <span className={`sx-chip sx-chip--sync-${(app.status.sync.status || 'unknown').toLowerCase()}`}>
                    <AppUtils.ComparisonStatusIcon status={app.status.sync.status} /> {app.status.sync.status}
                </span>
                <OperationState app={app} quiet={true} />
            </div>

            <dl className='sx-app-card__meta'>
                <dt>Source</dt>
                <dd>
                    <Tooltip content={source?.repoURL || ''} zIndex={4}>
                        <span>{shortRepoURL(source?.repoURL)}</span>
                    </Tooltip>
                    <span className='sx-app-card__rev'>@{targetRevision}</span>
                </dd>
                {(source?.path || source?.chart) && (
                    <>
                        <dt>{source?.chart ? 'Chart' : 'Path'}</dt>
                        <dd title={source?.chart || source?.path}>{source?.chart || source?.path}</dd>
                    </>
                )}
                <dt>Cluster</dt>
                <dd>
                    <Cluster server={app.spec.destination.server} name={app.spec.destination.name} />
                </dd>
                {labels.length > 0 && (
                    <>
                        <dt>Labels</dt>
                        <dd title={labels.join(', ')}>{labels.join(', ')}</dd>
                    </>
                )}
            </dl>

            <div className='sx-app-card__foot'>
                <span className='sx-app-card__time' title={`Created ${AppUtils.formatCreationTimestamp(app.metadata.creationTimestamp)}`}>
                    <i className='far fa-clock' aria-hidden='true' />{' '}
                    {lastSync ? `Synced ${moment(lastSync).fromNow()}` : `Created ${moment(app.metadata.creationTimestamp).fromNow()}`}
                </span>
                <div className='sx-app-card__actions applications-list__entry--actions'>
                    <button
                        type='button'
                        className='sx-action'
                        qe-id='applications-tiles-button-sync'
                        onClick={stop(() => syncApplication(app.metadata.name, app.metadata.namespace))}
                        title='Sync'>
                        <i className='fa fa-sync' /> Sync
                    </button>
                    <button
                        type='button'
                        className='sx-action sx-action--icon'
                        qe-id='applications-tiles-button-refresh'
                        {...AppUtils.refreshLinkAttrs(app)}
                        onClick={stop(() => refreshApplication(app.metadata.name, app.metadata.namespace))}
                        title='Refresh'
                        aria-label='Refresh'>
                        <i className={classNames('fa fa-redo', {'status-icon--spin': AppUtils.isAppRefreshing(app)})} />
                    </button>
                    <button
                        type='button'
                        className='sx-action sx-action--icon sx-action--danger'
                        qe-id='applications-tiles-button-delete'
                        onClick={stop(() => deleteApplication(app.metadata.name, app.metadata.namespace))}
                        title='Delete'
                        aria-label='Delete'>
                        <i className='fa fa-trash-can' />
                    </button>
                </div>
            </div>
        </div>
    );
};

const shortRepoURL = (url?: string) =>
    (url || 'Unknown')
        .replace(/^(https?:\/\/|oci:\/\/|ssh:\/\/)/, '')
        .replace(/^git@([^:]+):/, '$1/')
        .replace(/\.git$/, '');
